import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Product } from '@setvi/shared/interfaces';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  getProductMetadataQuery,
  getSalesEnablementContentQuery
} from 'Services';
import { useClaudeMessages } from 'hooksv2/useAnthropic';
import { CustomerPreferencesInterface } from 'componentsV2/customer-preferences/interfaces';

enum Prompts {
  QUESTIONS = 'Generate an array of 12 thoughtful and relevant product-related questions that a prospective customer or stakeholder might have after reading the provided Product Data. Prioritize the questions in order of likely popularity or relevance based on common customer concerns and buying behavior. Return exactly an array of 12 strings, no additional text or formatting.'
}

export interface ConversationProps {
  question: string;
  answer: string;
}

const formatProfile = (profile: CustomerPreferencesInterface) =>
  Object.entries(profile)
    .map(([key, value]) => `- ${key}: ${value}`)
    .join('\n');

const returnParseArray = (val: string) => {
  const start = val.indexOf('['); // Find the index where the array starts
  const end = val.indexOf(']'); // Find the index where the array ends

  if (!end || end === -1) return '';

  const arrayString = val.substring(start, end + 1); // Extract the array string

  return JSON.parse(arrayString);
};

const cleanAndStripHTML = (data: string) => {
  if (typeof data === 'string') {
    // Strip HTML tags and trim the result
    const text = data.replace(/<[^>]+>/g, '').trim();
    return text === '' ? undefined : text;
  }

  return data; // Return numbers, booleans, etc. as-is
};

export const useProductAssistant = ({
  product,
  customerPreferences
}: {
  product: Product;
  customerPreferences?: CustomerPreferencesInterface;
}) => {
  const { user } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [questions, setQuestions] = useState<string[]>([]);
  const [conversation, setConversation] = useState<ConversationProps[]>([]);

  const params = useMemo(
    () => ({
      manufacturerName: product?.vendor?.name,
      manufacturerSku: user?.IsManufacturer
        ? product?.sku
        : product?.manufacturerSku,
      ...(product?.name && { productName: product?.name })
    }),
    [user, product]
  );

  const enabled = useMemo(() => {
    if (!params?.manufacturerName || !params?.manufacturerSku) return false;
    if (!user?.IsManufacturer && product?.sku === product?.manufacturerSku)
      return false;

    return true;
  }, [params, user, product]);

  const { data, isFetching } = useQuery({
    ...getSalesEnablementContentQuery(
      product?._id,
      product?.categories?.[0]?._id
    ),
    enabled: !!product?._id,
    retry: false
  });

  const salesKit = useMemo(() => {
    const kit = data?.data?.flatMap((item: any) =>
      !!item?.data && Array.isArray(item?.data)
        ? item?.data?.map((entry: any) => ({
            name: typeof entry.name === 'string' ? entry.name : '',
            content: typeof entry.content === 'string' ? entry.content : ''
          }))
        : ''
    );

    return JSON.stringify(kit);
  }, [data]);

  const { data: metadata, isFetching: isMetadataFetching } = useQuery({
    ...getProductMetadataQuery(params),
    enabled
  });

  const globalPrompt = useMemo(() => {
    let prompt = metadata
      ? `Product Data: ${metadata}.\n`
      : JSON.stringify({
          name: product?.name || '',
          images: product?.images,
          manufacturerSku: product?.manufacturerSku || '',
          vendor: product?.vendor || '',
          custom: product?.custom || ''
        });

    if (salesKit) {
      prompt = `${prompt}. Product Sales Kit: ${cleanAndStripHTML(salesKit)}\n`;
    }

    if (customerPreferences) {
      prompt += `You are a helpful product advisor for veterinary clinics. Use the following context to answer questions in a personalized way:\n\n.
                 Here is the customer profile: \n${formatProfile(customerPreferences)} \n
                 \nPlease tailor responses to this customer’s preferences and clinic needs.`;
    }

    return prompt;
  }, [metadata, product, salesKit, customerPreferences]);

  const props = useMemo(() => {
    if (isFetching || isMetadataFetching) return null;

    return {
      tokens: 3024,
      stream: false,
      type: 'product' as const,
      fileBase: globalPrompt
    };
  }, [globalPrompt, isFetching, isMetadataFetching]);

  const { stream, asyncResponse } = useClaudeMessages({});
  const {
    stream: questionsStream,
    error: questionsError,
    loading: questionsLoading,
    onRegenerate: refreshQuestions
  } = useClaudeMessages({
    prompt: Prompts.QUESTIONS,
    ...props
  });

  const onSubmit = async (val: string) => {
    setLoading(true);
    setConversation(prev => [
      ...prev,
      {
        question: val,
        answer: ''
      }
    ]);
    asyncResponse({
      asHtml: true,
      type: 'product',
      prompt: val,
      tokens: 5024,
      // prevChat: conversation
      //   .flatMap(c => [c.question, c.answer])
      //   .filter(Boolean),
      fileBase: globalPrompt
    });
  };

  useEffect(() => {
    if (stream) {
      if (loading) setLoading(false);
      const lastConvo = conversation?.[conversation?.length - 1];
      const convo = {
        question: lastConvo?.question,
        answer: stream
      };

      setConversation(prev => prev.slice(0, -1).concat(convo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  useEffect(() => {
    if (questionsStream) {
      try {
        const res = returnParseArray(questionsStream);
        setQuestions(prev => res || prev);
        if (res?.length) setInitLoading(false);
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsStream]);

  return {
    loading,
    error: questionsError,
    initLoading: initLoading || isFetching || isMetadataFetching,
    questions,
    conversation,
    questionsLoading,
    refreshQuestions,
    onSubmit
  };
};
